import { ReactNode } from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Typography,
  SxProps,
  Theme,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

interface AccordionSectionProps {
  panelId: string;
  expanded: string | false;
  onExpandChange: (panel: string) => void;
  icon: ReactNode;
  title: string;
  count?: number;
  headerActions?: ReactNode;
  children: ReactNode;
  detailsSx?: SxProps<Theme>;
}

/**
 * Reusable accordion section with icon + title header.
 * Use `headerActions` for right-aligned buttons inside the summary row.
 * Use `detailsSx` to override AccordionDetails padding (e.g. `{ p: 0 }`).
 */
export function AccordionSection({
  panelId,
  expanded,
  onExpandChange,
  icon,
  title,
  count,
  headerActions,
  children,
  detailsSx,
}: AccordionSectionProps) {
  return (
    <Accordion
      expanded={expanded === panelId}
      onChange={() => onExpandChange(panelId)}
      elevation={0}
      variant="outlined"
      sx={{ borderRadius: 2, '&:before': { display: 'none' }, overflow: 'hidden' }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            width: '100%',
            justifyContent: 'space-between',
            gap: 1,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            {icon}
            <Typography fontWeight={700}>
              {title}
              {count !== undefined ? ` (${count})` : ''}
            </Typography>
          </Box>
          {headerActions && (
            <Box sx={{ display: 'flex', gap: 1 }}>{headerActions}</Box>
          )}
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={detailsSx}>{children}</AccordionDetails>
    </Accordion>
  );
}
